import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Database,
  Upload,
  MoreVertical,
  Trash2,
  Loader2,
  Download,
} from 'lucide-react';
import { useDocumentTitle } from 'usehooks-ts';
import { toast } from 'sonner';
import { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import MainHeader from '@/components/layout/MainHeader';
import { Main } from '@/components/main';
import { UploadPopulationDialog } from '@/components/compliance/test-types/UploadPopulationDialog';
import { FileExtensionIcon } from '@/utils/file-icons';
import { useTheme } from '@/context/theme-context';
import type { PopulationType, PopulationDTO } from '@deepguide-ai/types';

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/compliance/populations/'
)({
  component: PopulationsPage,
});

// Population type display names
const POPULATION_TYPE_LABELS: Record<PopulationType, string> = {
  EMPLOYEES: 'Employees',
  TERMINATED_EMPLOYEES: 'Terminated Employees',
  NEW_HIRES: 'New Hires',
  CONTRACTORS: 'Contractors',
  USERS: 'System Users',
  PRIVILEGED_USERS: 'Privileged Users',
  ASSETS: 'Assets',
  VENDORS: 'Third-Party Vendors',
  CHANGES: 'Change Records',
  INCIDENTS: 'Incidents',
  VISITORS: 'Visitors',
  DEVICES: 'Devices',
  SERVERS: 'Servers',
  REPOSITORIES: 'Repositories',
  TICKETS: 'Tickets',
  CUSTOM: 'Custom',
};

function PopulationsPage() {
  useDocumentTitle('Populations - Compliance');

  const { theme } = useTheme();
  const queryClient = useQueryClient();

  // Dialog state
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PopulationDTO | null>(null);

  // Filter state
  const [typeFilter, setTypeFilter] = useState<PopulationType | 'all'>('all');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Queries
  const populationsQuery = useQuery(trpc.population.list.queryOptions({}));

  // Mutations
  const deletePopulationMutation = useMutation({
    ...trpc.population.delete.mutationOptions(),
    onSuccess: () => {
      toast.success('Population deleted successfully');
      queryClient.invalidateQueries({
        queryKey: trpc.population.list.queryKey({}),
      });
      setDeleteTarget(null);
    },
    onError: (error) => {
      toast.error('Failed to delete population');
      console.error('Failed to delete population:', error);
    },
  });

  const handleDeletePopulation = async () => {
    if (deleteTarget) {
      await deletePopulationMutation.mutateAsync({ id: deleteTarget.id });
    }
  };

  const handleDownload = (population: PopulationDTO) => {
    const link = document.createElement('a');
    link.href = population.fileUrl;
    link.download = population.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const allPopulations = useMemo(
    () => populationsQuery.data?.items ?? [],
    [populationsQuery.data?.items]
  );

  // Filter populations by type
  const filteredPopulations = useMemo(() => {
    if (typeFilter === 'all') return allPopulations;
    return allPopulations.filter((p) => p.type === typeFilter);
  }, [allPopulations, typeFilter]);

  // Paginate
  const totalCount = filteredPopulations.length;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const paginatedPopulations = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredPopulations.slice(start, start + itemsPerPage);
  }, [filteredPopulations, currentPage, itemsPerPage]);

  // Reset page when filter changes
  const handleTypeFilterChange = (value: string) => {
    setTypeFilter(value as PopulationType | 'all');
    setCurrentPage(1);
  };

  // Get unique types for filter dropdown
  const availableTypes = useMemo(() => {
    const types = new Set(allPopulations.map((p) => p.type));
    return Array.from(types).sort();
  }, [allPopulations]);

  return (
    <Main>
      <MainHeader />

      <div className="mx-auto box-content flex max-w-[1008px] flex-col px-6 pb-12">
        {/* Header section */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <div className="text-foreground mb-2 text-2xl leading-6 font-semibold">
              Populations
            </div>
            <div className="text-muted-foreground text-sm leading-5">
              Manage population lists for SOC 2 sampling workflows.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4" />
              Upload Population
            </Button>
          </div>
        </div>

        {/* Content */}
        {populationsQuery.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : allPopulations.length === 0 ? (
          <div className="text-center py-12 border rounded-xl bg-muted/20">
            <Database className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <div className="text-muted-foreground mb-4">
              No populations uploaded yet
            </div>
            <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
              Upload a CSV or Excel file containing your population data (e.g., employee list, asset inventory) to start sampling for SOC 2 testing.
            </p>
            <Button size="sm" onClick={() => setIsCreateDialogOpen(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Upload Your First Population
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {totalCount} population{totalCount !== 1 ? 's' : ''}
              </p>
              <div className="flex items-center gap-2">
                <Select value={typeFilter} onValueChange={handleTypeFilterChange}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {availableTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {POPULATION_TYPE_LABELS[type]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Table */}
            <div className="border rounded-lg overflow-x-auto">
              <Table className="min-w-[700px] table-fixed">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[180px]">Name</TableHead>
                    <TableHead className="w-[140px]">Type</TableHead>
                    <TableHead className="text-right w-[80px]">Records</TableHead>
                    <TableHead className="w-[100px]">As Of Date</TableHead>
                    <TableHead className="w-[200px]">Source File</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedPopulations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No populations match the selected filter
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedPopulations.map((population) => (
                      <TableRow key={population.id}>
                        <TableCell className="font-medium truncate overflow-hidden">
                          {population.name}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {POPULATION_TYPE_LABELS[population.type]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {population.recordCount.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {format(new Date(population.asOfDate), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell className="overflow-hidden">
                          <button
                            onClick={() => handleDownload(population)}
                            className="flex items-center gap-2 text-muted-foreground hover:text-foreground group w-full"
                            title={`Download ${population.filename}`}
                          >
                            <FileExtensionIcon
                              filename={population.filename}
                              className="h-4 w-4 shrink-0"
                              theme={theme === 'dark' ? 'dark' : 'light'}
                            />
                            <span className="truncate group-hover:underline">
                              {population.filename}
                            </span>
                          </button>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleDownload(population)}
                              >
                                <Download className="mr-2 h-4 w-4" />
                                Download
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => setDeleteTarget(population)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Showing {paginatedPopulations.length} of {totalCount} populations
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <div className="text-sm">
                    Page {currentPage} of {totalPages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Population Dialog */}
      <UploadPopulationDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Population</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteTarget?.name}&quot;? This will also delete all associated samples. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePopulation}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deletePopulationMutation.isPending}
            >
              {deletePopulationMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Main>
  );
}
